import React, { useState } from "react";
import { loginWithEmail, completeProfile } from "../api/authApi";
import { saveTokens } from "../utils/authStorage";

/**
 * Email + Password login modal, shown automatically before a payment
 * if the user is not logged in.
 *
 * Flow:
 *   1. "login"   — user enters email + password  → POST /api/auth/login/
 *   2. "profile" — only if step === "signup_incomplete" (new user who has
 *                  not set a full_name yet)       → POST /api/auth/complete-profile/
 */
export default function LoginModal({ onClose, onSuccess }) {
  const [step, setStep]           = useState("login");   // login | profile
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [fullName, setFullName]   = useState("");
  const [accessToken, setToken]   = useState(null);      // temp access for profile step
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  /* ── Step 1: Email + Password ─────────────────────────────────────── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await loginWithEmail(email, password);
      saveTokens(res.data.tokens);
      if (res.data.step === "login_complete") {
        onSuccess();
      } else {
        // New / incomplete profile — keep the token, ask for name
        setToken(res.data.tokens.access);
        setStep("profile");
      }
    } catch (err) {
      setError(err.message || "Login failed. Please check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2: Complete Profile (new users only) ──────────────────── */
  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await completeProfile({ full_name: fullName }, accessToken);
      saveTokens(res.data.tokens);
      onSuccess();
    } catch (err) {
      setError(err.message || "Could not save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={loading ? undefined : onClose}>
      <div
        className="km-stars"
        style={styles.card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Log in to continue"
      >
        {!loading && (
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        )}

        <div style={styles.badge}>✦ Sign in</div>

        <h2 style={styles.heading}>
          {step === "login"   && "Log in to continue"}
          {step === "profile" && "One last thing"}
        </h2>
        <p style={styles.sub}>
          {step === "login"   && "Enter your registered email and password."}
          {step === "profile" && "Tell us your name to finish setting up your account."}
        </p>

        {error && <div style={styles.errorBox}>{error}</div>}

        {/* ── Login form ───────────────────────────────────────── */}
        {step === "login" && (
          <form onSubmit={handleLogin}>
            <input
              id="login-email"
              style={styles.input}
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
            />
            <input
              id="login-password"
              style={styles.input}
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button
              type="submit"
              id="login-submit-btn"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        )}

        {/* ── Profile completion (new users) ───────────────────── */}
        {step === "profile" && (
          <form onSubmit={handleCompleteProfile}>
            <input
              id="profile-fullname"
              style={styles.input}
              type="text"
              placeholder="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus
            />
            <button
              type="submit"
              style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
              disabled={loading}
            >
              {loading ? "Saving…" : "Continue"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(23, 15, 46, 0.55)",
    backdropFilter: "blur(3px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex: 1100, padding: 16,
  },
  card: {
    position: "relative", width: "100%", maxWidth: 360,
    background: "linear-gradient(165deg, #241a47 0%, #170F2E 100%)",
    borderRadius: "var(--radius-lg)", padding: "30px 26px 26px",
    color: "#f4ead4", boxShadow: "var(--shadow-pop)",
    border: "1px solid rgba(232,169,74,0.25)",
    animation: "km-pop 0.22s var(--ease)",
    textAlign: "center",
  },
  closeBtn: {
    position: "absolute", top: 14, right: 16, background: "none", border: "none",
    color: "#c9bfe6", fontSize: 24, cursor: "pointer", lineHeight: 1,
  },
  badge: {
    display: "inline-block", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase",
    color: "#E8A94A", border: "1px solid rgba(232,169,74,0.4)", borderRadius: 999,
    padding: "4px 12px", marginBottom: 14,
  },
  heading: { fontFamily: "var(--font-display)", fontSize: 21, margin: "0 0 8px", color: "#fbf5e9" },
  sub: { fontSize: 13.5, color: "#c9bfe6", margin: "0 0 20px", lineHeight: 1.5 },
  input: {
    width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(232,169,74,0.3)",
    background: "rgba(255,255,255,0.06)", color: "#fbf5e9", fontSize: 15, marginBottom: 14,
    boxSizing: "border-box", textAlign: "left",
  },
  btn: {
    width: "100%", padding: "13px 18px", borderRadius: 999, border: "none",
    background: "linear-gradient(135deg, #E8A94A, #D8622B)", color: "#1c1109",
    fontWeight: 700, fontSize: 14.5, cursor: "pointer",
  },
  btnDisabled: { opacity: 0.75, cursor: "wait" },
  errorBox: {
    background: "rgba(179,64,47,0.18)", border: "1px solid rgba(179,64,47,0.5)",
    color: "#f3c9c1", borderRadius: 8, padding: "10px 12px", fontSize: 13,
    marginBottom: 16, textAlign: "left",
  },
};
